import React, { useState, useRef } from 'react';
import { Btn } from '../UI/Btn';
import { Field, Inp } from '../UI/Inp';
import { useStore } from '../../hooks/useStore';
import { store } from '../../utils/store';
import { fmtPhone } from '../../utils/helpers';

// Converte imagem para WebP (igual ao Sidebar)
function converterParaWebP(file, maxSize = 400) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let w = img.width, h = img.height;
      if (w > maxSize || h > maxSize) {
        if (w > h) { h = Math.round(h * maxSize / w); w = maxSize; }
        else       { w = Math.round(w * maxSize / h); h = maxSize; }
      }
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      canvas.toBlob(blob => {
        if (!blob) { reject(new Error('Falha ao converter')); return; }
        resolve(new File([blob], 'logo.webp', { type: 'image/webp' }));
      }, 'image/webp', 0.85);
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Imagem inválida')); };
    img.src = url;
  });
}

export function Perfil() {
  const { user, logo, acesso } = useStore();
  const [nome,    setNome]    = useState(user?.nome    || '');
  const [empresa, setEmpresa] = useState(user?.empresa || '');
  const [wa,      setWa]      = useState(user?.wa      || '');
  const [err,     setErr]     = useState('');
  const [ok,      setOk]      = useState('');
  const [loading, setLoading] = useState(false);
  const [fotoLoading, setFotoLoading] = useState(false);

  // Modal de exclusão
  const [modalExcluir, setModalExcluir] = useState(false);
  const [confirmText,  setConfirmText]  = useState('');
  const [excluindo,    setExcluindo]    = useState(false);

  const fileRef = useRef();

  const handleFoto = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { setErr('Máximo 5MB.'); return; }
    e.target.value = '';
    setFotoLoading(true);
    setErr('');
    try {
      const webp = await converterParaWebP(f);
      await store.uploadLogo(webp);
      setOk('Foto atualizada!');
      setTimeout(() => setOk(''), 3000);
    } catch (e) {
      setErr('Erro ao salvar foto: ' + e.message);
    } finally {
      setFotoLoading(false);
    }
  };

  const handleSalvar = async () => {
    setErr(''); setOk('');
    if (!nome.trim())    { setErr('Informe seu nome.');           return; }
    if (!empresa.trim()) { setErr('Informe o nome do negócio.');  return; }
    setLoading(true);
    try {
      await store.atualizarPerfil({ nome: nome.trim(), empresa: empresa.trim(), wa });
      setOk('Perfil atualizado com sucesso!');
      setTimeout(() => setOk(''), 3000);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExcluir = async () => {
    if (confirmText !== 'EXCLUIR') { setErr('Digite EXCLUIR para confirmar.'); return; }
    setExcluindo(true);
    try {
      await store.excluirConta();
      window.location.reload();
    } catch (e) {
      setErr('Erro ao excluir: ' + e.message);
      setExcluindo(false);
    }
  };

  return (
    <div style={{ maxWidth: 560, margin: '0 auto' }}>

      {/* Card principal */}
      <div style={card}>
        <h2 style={titulo}>Perfil do Estabelecimento</h2>
        <p style={subtitulo}>Edite as informações do seu negócio</p>

        {err && <div style={alerta('erro')}>⚠️ {err}</div>}
        {ok  && <div style={alerta('ok')}>✅ {ok}</div>}

        {/* Foto */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, marginBottom: 28 }}>
          <div
            onClick={() => !fotoLoading && fileRef.current?.click()}
            style={{
              width: 100, height: 100, borderRadius: '50%',
              background: '#f1f5f9', border: '3px solid #e2e8f0',
              cursor: fotoLoading ? 'wait' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              overflow: 'hidden', position: 'relative',
              transition: 'border-color .15s',
            }}
          >
            {logo
              ? <img src={logo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <div style={{ textAlign: 'center', color: '#94a3b8' }}>
                  <div style={{ fontSize: 32 }}>📷</div>
                  <div style={{ fontSize: 11, marginTop: 4 }}>Adicionar foto</div>
                </div>
            }
            {fotoLoading && (
              <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ color: '#fff', fontSize: 11 }}>Salvando...</span>
              </div>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFoto} />
          <div style={{ display: 'flex', gap: 10 }}>
            <button style={btnTexto('#2563eb')} onClick={() => fileRef.current?.click()} disabled={fotoLoading}>
              {logo ? 'Trocar foto' : 'Escolher foto'}
            </button>
            {logo && (
              <button style={btnTexto('#dc2626')} onClick={() => store.setLogo(null)}>
                Remover foto
              </button>
            )}
          </div>
          <p style={{ fontSize: 11, color: '#94a3b8', margin: 0 }}>JPG, PNG ou WebP • máx. 5MB • convertida para WebP automaticamente</p>
        </div>

        {/* Campos */}
        <Field label="Nome do Responsável" required>
          <Inp value={nome} onChange={e => setNome(e.target.value)} placeholder="Maria Silva" />
        </Field>
        <Field label="Nome do Negócio" required>
          <Inp value={empresa} onChange={e => setEmpresa(e.target.value)} placeholder="Salão Beleza Total" />
        </Field>
        <Field label="WhatsApp" hint="Com DDD, ex: (11) 99999-9999">
          <Inp value={wa} onChange={e => setWa(fmtPhone(e.target.value))} placeholder="(11) 99999-9999" maxLength={16} />
        </Field>
        <Field label="E-mail">
          <Inp value={user?.email || ''} disabled style={{ background: 'var(--g50)', color: 'var(--g400)' }} />
          <p style={{ fontSize: 11, color: 'var(--g400)', marginTop: 4 }}>O e-mail não pode ser alterado.</p>
        </Field>

        {/* Info assinatura */}
        {acesso && (
          <div style={{
            background: acesso.status === 'ativa' ? '#f0fdf4' : '#fffbeb',
            border: `0.5px solid ${acesso.status === 'ativa' ? '#86efac' : '#fde68a'}`,
            borderRadius: 10, padding: '12px 14px', marginBottom: 20, fontSize: 13,
            color: acesso.status === 'ativa' ? '#16a34a' : '#92400e',
          }}>
            {acesso.status === 'ativa'
              ? `✅ Assinatura ativa — plano ${acesso.plano}`
              : `⏳ Período gratuito — ${acesso.dias_trial_restantes} dia${acesso.dias_trial_restantes !== 1 ? 's' : ''} restante${acesso.dias_trial_restantes !== 1 ? 's' : ''}`
            }
          </div>
        )}

        <Btn onClick={handleSalvar} disabled={loading} style={{ width: '100%', justifyContent: 'center', padding: '11px' }}>
          {loading ? 'Salvando...' : 'Salvar alterações'}
        </Btn>
      </div>

      {/* Card de exclusão */}
      <div style={{ ...card, border: '0.5px solid #fecaca', marginTop: 20 }}>
        <h3 style={{ fontSize: 15, fontWeight: 600, color: '#dc2626', marginBottom: 6 }}>
          ⚠️ Zona de Perigo
        </h3>
        <p style={{ fontSize: 13, color: 'var(--g600)', marginBottom: 16, lineHeight: 1.6 }}>
          Ao excluir sua conta, todos os seus dados serão permanentemente apagados — clientes, profissionais, serviços, agendamentos e configurações. Esta ação <strong>não pode ser desfeita</strong>.
        </p>
        <button
          onClick={() => { setModalExcluir(true); setErr(''); setConfirmText(''); }}
          style={{
            background: '#fef2f2', color: '#dc2626',
            border: '0.5px solid #fecaca', borderRadius: 8,
            padding: '9px 20px', fontSize: 13, fontWeight: 600,
            cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          Excluir minha conta
        </button>
      </div>

      {/* Modal de confirmação de exclusão */}
      {modalExcluir && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000, padding: 16,
        }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 28, maxWidth: 420, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,.2)' }}>
            <div style={{ fontSize: 40, textAlign: 'center', marginBottom: 12 }}>🗑️</div>
            <h3 style={{ fontSize: 17, fontWeight: 700, color: '#111', textAlign: 'center', marginBottom: 8 }}>
              Excluir conta permanentemente?
            </h3>
            <p style={{ fontSize: 13, color: '#555', lineHeight: 1.6, marginBottom: 20, textAlign: 'center' }}>
              Você perderá <strong>todos os seus dados</strong> incluindo:
            </p>
            <ul style={{ fontSize: 13, color: '#666', marginBottom: 20, paddingLeft: 20, lineHeight: 2 }}>
              <li>Todos os clientes cadastrados</li>
              <li>Todos os profissionais e serviços</li>
              <li>Todo o histórico de agendamentos</li>
              <li>Configurações, horários e logo</li>
            </ul>
            <p style={{ fontSize: 13, color: '#dc2626', fontWeight: 600, marginBottom: 10 }}>
              Digite <strong>EXCLUIR</strong> para confirmar:
            </p>
            <input
              type="text"
              value={confirmText}
              onChange={e => setConfirmText(e.target.value.toUpperCase())}
              placeholder="EXCLUIR"
              style={{
                width: '100%', padding: '10px 12px', border: '1.5px solid #fca5a5',
                borderRadius: 8, fontSize: 14, fontFamily: 'inherit',
                outline: 'none', boxSizing: 'border-box', marginBottom: 16,
                textAlign: 'center', letterSpacing: 2, fontWeight: 700,
              }}
            />
            {err && <div style={{ ...alerta('erro'), marginBottom: 12 }}>⚠️ {err}</div>}
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => { setModalExcluir(false); setConfirmText(''); setErr(''); }}
                style={{
                  flex: 1, background: 'var(--g100)', color: 'var(--g700)',
                  border: '0.5px solid var(--g200)', borderRadius: 8,
                  padding: '10px', fontSize: 13, fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleExcluir}
                disabled={excluindo || confirmText !== 'EXCLUIR'}
                style={{
                  flex: 1,
                  background: confirmText === 'EXCLUIR' ? '#dc2626' : '#fca5a5',
                  color: '#fff', border: 'none', borderRadius: 8,
                  padding: '10px', fontSize: 13, fontWeight: 600,
                  cursor: confirmText === 'EXCLUIR' ? 'pointer' : 'not-allowed',
                  fontFamily: 'inherit', transition: 'background .15s',
                }}
              >
                {excluindo ? 'Excluindo...' : 'Confirmar exclusão'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const card = {
  background: '#fff',
  borderRadius: 'var(--r-lg)',
  border: '0.5px solid var(--g200)',
  boxShadow: '0 1px 4px rgba(0,0,0,.06)',
  padding: '24px 24px 20px',
};

const titulo = {
  fontSize: 17, fontWeight: 700, color: 'var(--g800)', marginBottom: 4,
};

const subtitulo = {
  fontSize: 13, color: 'var(--g500)', marginBottom: 24,
};

const alerta = (tipo) => ({
  background: tipo === 'ok' ? 'var(--green-l)' : 'var(--red-l)',
  color: tipo === 'ok' ? 'var(--green)' : 'var(--red)',
  border: `0.5px solid ${tipo === 'ok' ? '#86efac' : '#fca5a5'}`,
  borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 16,
});

const btnTexto = (cor) => ({
  background: 'none', border: 'none',
  color: cor, fontSize: 12, cursor: 'pointer',
  fontFamily: 'inherit', padding: 0,
});
